import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, Eye, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserReportsTab({ adminToken }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchReports = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set("search", searchTerm);
      if (filterStatus) params.set("status", filterStatus);
      const res = await axios.get(`${API}/admin/reports?${params.toString()}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setReports(res.data.reports || []);
    } catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [searchTerm, filterStatus, adminToken]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    try { await axios.delete(`${API}/admin/reports/${id}`, { headers: { Authorization: `Bearer ${adminToken}` } }); toast.success("Report deleted"); fetchReports(); }
    catch { toast.error("Failed"); }
  };

  const handleRerun = async (id) => {
    try { const res = await axios.post(`${API}/admin/reports/${id}/rerun`, {}, { headers: { Authorization: `Bearer ${adminToken}` } }); toast.success(res.data.message || "Cache cleared"); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card className="bg-white border-[#E5E7EB]">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="text-lg text-[#111827]">User Reports ({reports.length})</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchReports()} className="pl-9 w-56 bg-white border-[#E5E7EB]" data-testid="report-search-input" /></div>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-28 bg-white border-[#E5E7EB]"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="free">Free</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
            </Select>
            <Button size="sm" onClick={fetchReports} className="bg-[#3B5BDB] text-white"><Search className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E7EB] bg-[#F9FAFB]">
              <TableHead className="text-[#6B7280]">Business</TableHead>
              <TableHead className="text-[#6B7280]">User</TableHead>
              <TableHead className="text-center text-[#6B7280]">Score</TableHead>
              <TableHead className="text-center text-[#6B7280]">Status</TableHead>
              <TableHead className="text-[#6B7280]">Date</TableHead>
              <TableHead className="text-right text-[#6B7280]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.length > 0 ? reports.map((r, i) => (
              <TableRow key={r.id || i} className="border-[#E5E7EB]">
                <TableCell><p className="font-medium text-sm text-[#111827]">{r.business_name}</p><p className="text-xs text-[#9CA3AF]">{r.place_id?.slice(0, 20)}...</p></TableCell>
                <TableCell className="text-sm text-[#6B7280]">{r.user_email}</TableCell>
                <TableCell className="text-center"><Badge className={r.score >= 70 ? "bg-emerald-50 text-emerald-600" : r.score >= 40 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}>{r.score != null ? `${r.score}/100` : "N/A"}</Badge></TableCell>
                <TableCell className="text-center"><Badge className={r.status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}>{r.status === "paid" ? "Paid" : "Free"}</Badge></TableCell>
                <TableCell className="text-sm text-[#6B7280]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/report/${r.place_id}`)} className="text-[#3B5BDB]"><Eye className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleRerun(r.id)} className="text-blue-500"><RefreshCw className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#9CA3AF]">No reports found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
